import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { AutoLinkPlugin } from "@lexical/react/LexicalAutoLinkPlugin";
import { HeadingNode, QuoteNode, } from "@lexical/rich-text";
import { ListNode, ListItemNode, } from "@lexical/list";
import { CodeNode, CodeHighlightNode, } from "@lexical/code";
import { LinkNode, AutoLinkNode, } from "@lexical/link";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $createParagraphNode, $getRoot, $getSelection, $isRangeSelection, CAN_REDO_COMMAND, CAN_UNDO_COMMAND, COMMAND_PRIORITY_LOW, FORMAT_TEXT_COMMAND, KEY_DOWN_COMMAND, REDO_COMMAND, SELECTION_CHANGE_COMMAND, UNDO_COMMAND, } from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import DOMPurify from "dompurify";
import { FiBold, FiItalic, FiUnderline, FiCode, FiSmile, FiLoader, FiType, FiRotateCcw, FiRotateCw, } from "react-icons/fi";
import MentionsPlugin from "./MentionsPlugin";
import { MentionNode } from "./MentionNode";
import ExempleTheme from "@/features/shared/components/LexicalEditor/ExempleTheme";
import "@/features/shared/components/LexicalEditor/style.css";
const INITIAL_TOOLBAR_STATE = {
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isCode: false,
    canUndo: false,
    canRedo: false,
};
const defaultPlaceholder = "Partagez une mise à jour et mentionnez vos collègues.";
const EMOJI_OPTIONS = [
    "😀",
    "😁",
    "😂",
    "🤣",
    "😊",
    "😍",
    "🤔",
    "🙌",
    "🎉",
    "🚀",
    "👍",
    "🙏",
    "💡",
    "✅",
    "🔥",
];
const HASHTAG_MATCHER = (text) => {
    const regex = /(^|\s)#([\p{L}\p{N}_-]+)/gu;
    let match;
    let result = null;
    while ((match = regex.exec(text)) !== null) {
        const leading = match[1];
        const tag = match[2];
        const start = match.index + leading.length;
        const length = tag.length + 1;
        result = {
            index: start,
            length,
            text: `#${tag}`,
            url: `https://jira.local/browse/${tag.toUpperCase()}`,
        };
    }
    return result;
};
const ToolbarButton = ({ label, icon, active = false, onClick, disabled = false }) => (_jsx("button", { type: "button", "aria-label": label, onClick: onClick, disabled: disabled, className: `flex h-8 w-8 items-center justify-center rounded transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${disabled
        ? "cursor-not-allowed text-slate-300"
        : active
            ? "bg-blue-600 text-white"
            : "text-slate-600 hover:bg-slate-200"}`, children: icon }));
const Toolbar = () => {
    const [editor] = useLexicalComposerContext();
    const [state, setState] = useState(INITIAL_TOOLBAR_STATE);
    const [emojiMenuOpen, setEmojiMenuOpen] = useState(false);
    const emojiContainerRef = useRef(null);
    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            setState((current) => ({
                ...current,
                isBold: selection.hasFormat("bold"),
                isItalic: selection.hasFormat("italic"),
                isUnderline: selection.hasFormat("underline"),
                isCode: selection.hasFormat("code"),
            }));
        }
    }, []);
    useEffect(() => {
        return editor.registerCommand(SELECTION_CHANGE_COMMAND, () => {
            updateToolbar();
            return false;
        }, COMMAND_PRIORITY_LOW);
    }, [editor, updateToolbar]);
    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                updateToolbar();
            });
        });
    }, [editor, updateToolbar]);
    useEffect(() => {
        const unregisterUndo = editor.registerCommand(CAN_UNDO_COMMAND, (payload) => {
            setState((current) => ({ ...current, canUndo: payload }));
            return false;
        }, COMMAND_PRIORITY_LOW);
        const unregisterRedo = editor.registerCommand(CAN_REDO_COMMAND, (payload) => {
            setState((current) => ({ ...current, canRedo: payload }));
            return false;
        }, COMMAND_PRIORITY_LOW);
        return () => {
            unregisterUndo();
            unregisterRedo();
        };
    }, [editor]);
    useEffect(() => {
        if (!emojiMenuOpen) {
            return;
        }
        const handleClickOutside = (event) => {
            if (emojiContainerRef.current &&
                !emojiContainerRef.current.contains(event.target)) {
                setEmojiMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [emojiMenuOpen]);
    const handleEmojiSelect = (emoji) => {
        editor.focus();
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                selection.insertText(emoji);
            }
        });
        setEmojiMenuOpen(false);
    };
    return (_jsx("div", { className: "flex flex-wrap items-center justify-between gap-2 rounded-t-lg border border-slate-200 bg-slate-50 px-3 py-2", role: "toolbar", children: _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(ToolbarButton, { label: "Annuler", icon: _jsx(FiRotateCcw, {}), active: false, onClick: () => editor.dispatchCommand(UNDO_COMMAND, undefined), disabled: !state.canUndo }), _jsx(ToolbarButton, { label: "R\u00E9tablir", icon: _jsx(FiRotateCw, {}), active: false, onClick: () => editor.dispatchCommand(REDO_COMMAND, undefined), disabled: !state.canRedo }), _jsx("span", { className: "h-6 w-px bg-slate-200", "aria-hidden": "true" }), _jsx(ToolbarButton, { label: "Gras", icon: _jsx(FiBold, {}), active: state.isBold, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold") }), _jsx(ToolbarButton, { label: "Italique", icon: _jsx(FiItalic, {}), active: state.isItalic, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic") }), _jsx(ToolbarButton, { label: "Soulign\u00E9", icon: _jsx(FiUnderline, {}), active: state.isUnderline, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline") }), _jsx(ToolbarButton, { label: "Code", icon: _jsx(FiCode, {}), active: state.isCode, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code") }), _jsxs("div", { className: "relative", ref: emojiContainerRef, children: [_jsx(ToolbarButton, { label: "Emoji", icon: _jsx(FiSmile, {}), active: emojiMenuOpen, onClick: () => setEmojiMenuOpen((previous) => !previous) }), emojiMenuOpen && (_jsx("div", { className: "absolute left-0 top-full z-10 mt-2 grid w-56 grid-cols-6 gap-1 rounded border border-slate-200 bg-white p-2 text-base shadow-lg", children: EMOJI_OPTIONS.map((emoji) => (_jsx("button", { type: "button", className: "flex h-8 w-8 items-center justify-center rounded hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500", onMouseDown: (event) => event.preventDefault(), onClick: () => handleEmojiSelect(emoji), "aria-label": `Insérer l'emoji ${emoji}`, children: _jsx("span", { "aria-hidden": "true", children: emoji }) }, emoji))) }))] })] }) }));
};
const KeyboardShortcutsPlugin = ({ onSubmit, onCancel }) => {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        return editor.registerCommand(KEY_DOWN_COMMAND, (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                onSubmit();
                return true;
            }
            if (event.key === "Escape") {
                event.preventDefault();
                onCancel();
                return true;
            }
            return false;
        }, COMMAND_PRIORITY_LOW);
    }, [editor, onCancel, onSubmit]);
    return null;
};
const InitialContentPlugin = ({ html }) => {
    const [editor] = useLexicalComposerContext();
    const lastHtmlRef = useRef(null);
    useEffect(() => {
        if (html === null || html === lastHtmlRef.current) {
            return;
        }
        lastHtmlRef.current = html;
        editor.update(() => {
            const root = $getRoot();
            root.clear();
            if (!html) {
                root.append($createParagraphNode());
                return;
            }
            const parser = new DOMParser();
            const dom = parser.parseFromString(html, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);
            root.append(...nodes);
        });
    }, [editor, html]);
    return null;
};
const editorBaseConfig = {
    namespace: "task-comment-editor",
    theme: ExempleTheme,
    nodes: [
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        CodeNode,
        CodeHighlightNode,
        LinkNode,
        AutoLinkNode,
        MentionNode,
    ],
    onError(error) {
        console.error("Lexical error", error);
    },
};
const CommentEditor = ({ value, onChange, onSubmit, onCancel, mentionOptions, isSubmitting = false, disabled = false, placeholder = defaultPlaceholder, charLimit = 5000, submitLabel = "Publier", cancelLabel = "Annuler", }) => {
    const initialPlainText = useMemo(() => {
        if (!value)
            return "";
        const parser = new DOMParser();
        const dom = parser.parseFromString(value, "text/html");
        return dom.body.textContent ?? "";
    }, [value]);
    const [snapshot, setSnapshot] = useState({
        html: value,
        plainText: initialPlainText,
        characterCount: initialPlainText.trim().length,
    });
    const [localError, setLocalError] = useState(null);
    const hasHydratedRef = useRef(false);
    const initialContentHtml = useMemo(() => {
        if (!hasHydratedRef.current) {
            return value;
        }
        return value !== snapshot.html ? value : null;
    }, [snapshot.html, value]);
    useEffect(() => {
        hasHydratedRef.current = true;
    }, []);
    useEffect(() => {
        if (!hasHydratedRef.current || value === snapshot.html) {
            return;
        }
        const parser = new DOMParser();
        const dom = parser.parseFromString(value, "text/html");
        const plainText = dom.body.textContent ?? "";
        setSnapshot({
            html: value,
            plainText,
            characterCount: plainText.trim().length,
        });
    }, [snapshot.html, value]);
    const handleEditorChange = useCallback((editorState, editor) => {
        editorState.read(() => {
            const plainText = $getRoot().getTextContent();
            const characterCount = plainText.trim().length;
            const html = $generateHtmlFromNodes(editor);
            const sanitizedHtml = DOMPurify.sanitize(html);
            const snapshotPayload = {
                html: sanitizedHtml,
                plainText,
                characterCount,
            };
            setSnapshot(snapshotPayload);
            onChange(snapshotPayload);
        });
    }, [onChange]);
    const canSubmit = snapshot.characterCount <= charLimit && snapshot.characterCount > 0;
    const handleSubmit = useCallback(async () => {
        if (!canSubmit)
            return;
        setLocalError(null);
        try {
            await onSubmit(snapshot);
        }
        catch (error) {
            setLocalError(error.message ?? "Impossible de publier le commentaire");
        }
    }, [canSubmit, onSubmit, snapshot]);
    return (_jsxs("div", { className: "rounded-lg border border-slate-200 bg-white shadow-sm", role: "form", children: [_jsxs(LexicalComposer, { initialConfig: editorBaseConfig, children: [_jsx(Toolbar, {}), _jsxs("div", { className: "border-b border-slate-200", children: [_jsx(InitialContentPlugin, { html: initialContentHtml }), _jsx(RichTextPlugin, { contentEditable: _jsx(ContentEditable, { className: "min-h-[160px] w-full resize-none rounded-b-lg px-4 py-3 text-sm leading-relaxed text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500", "aria-placeholder": placeholder, placeholder: () => null }), placeholder: _jsx("div", { className: "pointer-events-none select-none px-4 py-3 text-sm text-slate-400", children: placeholder }), ErrorBoundary: LexicalErrorBoundary }), _jsx(HistoryPlugin, {}), _jsx(LinkPlugin, {}), _jsx(AutoLinkPlugin, { matchers: [HASHTAG_MATCHER] }), _jsx(OnChangePlugin, { onChange: handleEditorChange }), _jsx(MentionsPlugin, { options: mentionOptions }), _jsx(KeyboardShortcutsPlugin, { onSubmit: () => void handleSubmit(), onCancel: onCancel })] })] }), _jsxs("footer", { className: "flex flex-wrap items-center justify-between gap-3 px-4 py-3", children: [_jsxs("div", { className: `flex items-center gap-2 text-xs ${snapshot.characterCount > charLimit
                            ? "text-red-600"
                            : "text-slate-500"}`, children: [_jsx(FiType, { "aria-hidden": "true" }), _jsxs("span", { children: [snapshot.characterCount, "/", charLimit, " caract\u00E8res"] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { type: "button", onClick: onCancel, className: "rounded px-3 py-1 text-sm font-medium text-slate-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500", children: cancelLabel }), _jsxs("button", { type: "button", onClick: () => void handleSubmit(), className: `flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${(!canSubmit || disabled || isSubmitting)
                                    ? "cursor-not-allowed opacity-70"
                                    : ""}`, disabled: !canSubmit || disabled || isSubmitting, children: [isSubmitting && _jsx(FiLoader, { className: "animate-spin" }), submitLabel] })] })] }), localError && (_jsx("div", { className: "px-4 pb-3 text-sm text-red-600", role: "alert", "aria-live": "assertive", children: localError }))] }));
};
export default CommentEditor;
